/**
 *  Component For Providers Login
 */
import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import Axios from "axios";
import { useHistory, useLocation } from "react-router-dom";
import "../../styles/LoginProviders.css";
import "../../styles/LoginPatients.css";
import LoadingSpin from "../utils/Loading";
import Copyright from "../utils/Copyright";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
/** Redux resources */
import { setUserAction } from "../../actions";
import { useDispatch } from "react-redux";
/** Matetial UI New Imports */
import {
  Button,
  TextField,
  FormControlLabel,
  Checkbox,
  Link,
  Grid,
  Typography,
  Avatar,
  Box,
  IconButton,
  InputAdornment,
  Paper,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Alert } from "@material-ui/lab";
import useBDocTranslator from "../hooks/useBDocTranslator";
import { loginValidationSchema } from "../../utils/validationSchemas";
import TranslationButton from "../utils/TranslationButton";
import Visibility from "@material-ui/icons/Visibility";
import VisibilityOff from "@material-ui/icons/VisibilityOff";

const useStyles = makeStyles((theme) => ({
  margin: {
    margin: theme.spacing(1),
  },
  extendedIcon: {
    marginRight: theme.spacing(1),
  },
  form: {
    width: "100%", // Fix IE 11 issue.
    marginTop: theme.spacing(1),
  },
  submit: {
    margin: theme.spacing(3, 0, 2),
    borderRadius: 20,
  },
}));

function LoginMain(props) {
  const classes = useStyles();
  const location = useLocation();
  const [errorMessage, setErrorMessage] = useState({
    error: "",
    visible: false,
    verified: false,
  });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Formik configuration
  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
    },
    validationSchema: loginValidationSchema,
    onSubmit: (values) => {
      handleLogin(values.email, values.password);
    },
  });

  const queryType = "3"; // for Role ID 3 (provider)
  let history = useHistory();

  //Distpach of Redux
  const distpach = useDispatch();

  // To manage translations
  const { t, onChangeLanguaje, changeDirectLanguage } = useBDocTranslator();

  const Logo = process.env.REACT_APP_TENANT_LOGO;

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleLogin = (email, password) => {
    setLoading(true);
    setErrorMessage({ error: "", visible: false, verified: false });

    Axios.post(`${process.env.REACT_APP_API_URL_BASE}/session`, {
      email,
      password,
      queryType,
    })
      .then((response) => {
        if (response.data) {
          //distpach values to main store
          console.log({
            RESPONSE: response.data,
          });
          console.log("User time_zone:", response.data.user?.time_zone);
          distpach(setUserAction(response.data));
          if (location.state?.from) {
            history.push(location.state?.from);
            return;
          }
          history.push("/admin/dashboard");
        }
      })
      .catch((error) => {
        setLoading(false);
        console.log(error);
        setErrorMessage({
          error:
            error?.response?.data?.message ||
            "User not allowed, please contact tenant admin.",
          visible: true,
        });
      });
  };

  const handleClick = (e) => {
    e.preventDefault();
    formik.handleSubmit();
  };

  const handleCancel = () => {
    history.push("/");
  };

  useEffect(() => {
    changeDirectLanguage("es");
  }, []);

  return (
    <div className="container-fluid">
      <Paper elevation={2} className="row p-3 vh-100">
        {/* Left column - Side image (hidden on mobile) */}
        <div className="col-md-7 d-none d-md-flex justify-content-center provider-login-side-image rounded"></div>

        {/* Right column - Login form */}
        <div className="col-md-5 d-flex flex-column justify-content-center px-4">
          {/* Top bar */}
          <div className="d-flex justify-content-between align-items-center mb-3">
            <IconButton
              aria-label="back"
              className={classes.margin}
              fontSize="large"
              onClick={handleCancel}
            >
              <ArrowBackIcon />
            </IconButton>
            <TranslationButton onChangeLanguaje={onChangeLanguaje} />
          </div>

          {/* Logo */}
          <div className="d-flex justify-content-center">
            <div className="icon-login-container">
              <Avatar className="icon-login-avatar" src={Logo}></Avatar>
            </div>
          </div>

          {/* Title */}
          <Typography component="h1" variant="h5" align="center">
            {t("login.provider.signin")}
          </Typography>

          {/* Register subtitle */}
          <h5 className="text-center mt-2">
            {t("login.patient.new.here") + "? "}
            <Link href="/admin/provider/registration" variant="inherit">
              {t("login.patient.register")}
            </Link>
          </h5>

          {/* Error alert */}
          <div className="row mt-2 justify-content-center">
            {errorMessage.visible && (
              <Alert severity="error">{errorMessage.error}</Alert>
            )}
          </div>

          {/* Login form */}
          <form className={classes.form} noValidate onSubmit={formik.handleSubmit}>
            <TextField
              variant="outlined"
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email"
              name="email"
              autoComplete="email"
              autoFocus
              value={formik.values.email}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.email && Boolean(formik.errors.email)}
              helperText={formik.touched.email && formik.errors.email}
            />
            <TextField
              variant="outlined"
              margin="normal"
              required
              fullWidth
              name="password"
              label={t("password")}
              type={showPassword ? "text" : "password"}
              id="password"
              autoComplete="current-password"
              value={formik.values.password}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.password && Boolean(formik.errors.password)}
              helperText={formik.touched.password && formik.errors.password}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowPassword}
                      edge="end"
                    >
                      {showPassword ? <Visibility /> : <VisibilityOff />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <FormControlLabel
              control={<Checkbox value="remember" color="primary" />}
              label={t("login.patient.rememberme")}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              className={classes.submit}
              onClick={handleClick}
            >
              {t("login.signin")}
            </Button>
            <Grid container className="mt-2">
              <Grid item xs>
                <Link href="/admin/login/recovery" variant="body2">
                  {t("login.patient.forgot.password")}
                </Link>
              </Grid>
            </Grid>
            <div>
              {loading && (
                <div className="d-flex justify-content-center">
                  <LoadingSpin type="spinningBubbles" color="#1d3557" />
                </div>
              )}
            </div>
          </form>
          <Box mt={4}>
            <Copyright />
          </Box>
        </div>
      </Paper>
    </div>
  );
}

export default LoginMain;
